/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { UserContext } from "components/context/UserContext";
import PropTypes from "prop-types";

import { fetchMyAnnouncements } from "../../services/announcementService";
import "../../sass/zeplin/ClinicHeader.scss";
import "./reps/reps.css";
import "./common.css";
import { SOCKET_EVENTS } from "constants/socketEvents";
import useContainerWidth from "hooks/useContainerWidth";
import Announcement from "./Announcement";
import { ROLE_NAMES } from "helpers/common";
import MyRepCaresPanel from "components/common/myRepCares/MyRepCaresPanel";
import ReferralsButton from "components/rep/referral/ReferralsButton";
import { fetchUnreadMessages } from "services/chatService";
import { delegateUserRoutes } from "routes";
import NewNavLink from "./NewNavLink";
import NewNavLinkSidebar from "./NewNavLinkSidebar";

const LINK_WIDTH = 120; // Updated to match fixed width of nav items (120px + any margins/gaps)
const RESERVED_SPACE = 450; // Space for logo, notifications, messages, etc.
const logoImageUrl =
  "https://myrepapp-assets.s3-us-west-1.amazonaws.com/271f67d25e15bf1f1ba0e3ce5662b552-newlogo.png";
export const NavLink = ({
  pathname,
  Img,
  name,
  description, // <-- NEW
  route,
  onClick = null,
  ...rest
}) => {
  const isSelected = route?.includes(pathname);

  const contents = (
    <>
      <Img stroke={isSelected ? "#00adec" : "#224984"} />
      <span className="nav-link-name">{name}</span>
      {description ? (
        <span className="nav-link-desc" aria-label={description}>
          {description}
        </span>
      ) : null}
    </>
  );

  if (typeof onClick === "function") {
    return (
      <a onClick={onClick} href="#" {...rest}>
        {contents}
      </a>
    );
  }

  return (
    <Link to={pathname} className={isSelected ? "--selected" : ""} {...rest}>
      {contents}
    </Link>
  );
};

const NavHeader = ({ links, renderLinks }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { state, dispatch } = useContext(UserContext);
  const { currentUser, socketHandler } = state;
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [componentsLoaded, setComponentsLoaded] = useState(false);

  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const navbarRef = useRef(null);
  const [initialWidth, setInitialWidth] = useState(window.innerWidth - 200); // 200px accounts for margins and padding
  const width = useContainerWidth(navbarRef) || initialWidth;

  const isRep = useMemo(
    () =>
      [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER].includes(
        currentUser?.roleName,
      ),
    [currentUser],
  );
  const showOfficeLogo = useMemo(() => {
    if (
      [ROLE_NAMES.DELEGATED_USER].includes(currentUser?.roleName) &&
      currentUser?.roleId === 8 &&
      pathname !== "/impersonation-options"
    ) {
      return true;
    }
    return false;
  }, [currentUser, pathname]);

  // Check if current route is a delegate-only route
  const isDelegateOnlyRoute = useMemo(() => {
    return delegateUserRoutes.some((route) => {
      if (route.path.includes("*")) {
        const basePath = route.path.replace("/*", "");
        return pathname.startsWith(basePath);
      }
      return pathname === route.path;
    });
  }, [pathname]);

  // Determine if we should show announcements and messages
  const shouldShowNotifications = useMemo(() => {
    // If user is a delegate
    if (
      currentUser?.roleName === ROLE_NAMES.DELEGATED_USER &&
      isDelegateOnlyRoute
    ) {
      // If they're on a delegate-only route AND haven't impersonated an office
      return false;
    }
    return true;
  }, [currentUser, isDelegateOnlyRoute]);

  useEffect(() => {
    const handleResize = () => {
      setInitialWidth(window.innerWidth - 200);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchUnreadAnnouncements = async (refetch = false) => {
    const timeElapsed =
      (Date.now() - state.lastAnnouncementFetchTime) / (1000 * 60);

    if (!refetch && timeElapsed < 10) return;

    try {
      const response = await fetchMyAnnouncements(1, 5);

      dispatch({
        type: "SET_ANNOUNCEMENTS",
        payload: {
          announcements: response.data.announcements,
          pagination: response.data.pagination,
          unreadCount: response.data.unreadCount,
        },
      });
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  const fetchMessageNotification = async (refetch = false) => {
    const timeElapsed =
      (Date.now() - state.lastMessagesFetchTime) / (1000 * 60);

    if (!refetch && timeElapsed < 10) return;

    try {
      const response = await fetchUnreadMessages();

      dispatch({
        type: "SET_MESSAGE_NOTIFICATIONS",
        payload: {
          messageNotifications: response?.data || [],
          lastMessagesFetchTime: Date.now(),
        },
      });
    } catch (error) {
      console.error("Error fetching unread messages:", error);
    }
  };

  const unReadCount = useMemo(() => {
    const announcementCount = Number(state.unreadAnnouncementsCount || 0);
    const messageCount = Array.isArray(state.messageNotifications)
      ? state.messageNotifications.length
      : 0;

    return announcementCount + messageCount;
  }, [state.unreadAnnouncementsCount, state.messageNotifications]);
  useEffect(() => {
    if (!socketHandler) return;

    const handleNewAnnouncement = (data) => {
      console.log("New Announcement", { data });

      if (data.newAnnouncement && data.announcement) {
        dispatch({
          type: "ADD_ANNOUNCEMENT",
          payload: { announcement: data.announcement },
        });
      }
      if (data.newChatMessage && data.message) {
        dispatch({
          type: "APPEND_MESSAGE_NOTIFICATIONS",
          payload: { messageNotification: data.message },
        });
      }
    };

    socketHandler.on(SOCKET_EVENTS.NOTIFICATION.NEW, handleNewAnnouncement);

    return () => {
      socketHandler.off(SOCKET_EVENTS.NOTIFICATION.NEW, handleNewAnnouncement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketHandler]);

  const hasFetchedAnnouncements = useRef(false);

  useEffect(() => {
    if (!currentUser?.id) return;
    if (hasFetchedAnnouncements.current) return;
    if (!shouldShowNotifications) return; // Don't fetch if we shouldn't show notifications

    const timer = setTimeout(() => {
      fetchUnreadAnnouncements();
      fetchMessageNotification();
      setComponentsLoaded(true);
      hasFetchedAnnouncements.current = true;
      // console.log("refetching from timer");
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, shouldShowNotifications]);

  useEffect(() => {
    if (!state?.announcementsVersion) return;
    if (!shouldShowNotifications) return; // Don't refetch if we shouldn't show notifications
    fetchUnreadAnnouncements();
    // console.log("refetching from announcementsVersion");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.announcementsVersion, shouldShowNotifications]);

  const toggleNotifications = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up
    setIsNotificationsOpen(!isNotificationsOpen);
  };

  const navBarLinks = useMemo(() => {
    if (!links?.length) {
      return { available: [], overflow: [] };
    }

    // If screen width is less than 530px, put everything in overflow except appointments
    if (width < 530) {
      const appointmentsLink = links.find(
        (l) => l.pathname === "/rep/appointments",
      );
      return {
        available: appointmentsLink ? [appointmentsLink] : [],
        overflow: links.filter((l) => l.pathname !== "/rep/appointments"),
      };
    }

    const availableWidth = (width || initialWidth) - RESERVED_SPACE;
    const maxLinks = Math.max(1, Math.floor(availableWidth / LINK_WIDTH));

    // First ensure appointments link is in available array
    const appointmentsLink = links.find(
      (l) => l.pathname === "/rep/appointments",
    );
    const otherLinks = links.filter((l) => l.pathname !== "/rep/appointments");

    // Calculate how many other links we can fit
    const remainingSlots = appointmentsLink ? maxLinks - 1 : maxLinks;

    return {
      available: appointmentsLink
        ? [appointmentsLink, ...otherLinks.slice(0, remainingSlots)]
        : otherLinks.slice(0, maxLinks),
      overflow: appointmentsLink
        ? otherLinks.slice(remainingSlots)
        : otherLinks.slice(maxLinks),
    };
  }, [links, width, initialWidth]);

  return (
    <div
      className="NavHeader"
      data-cy="nav-head"
      ref={navbarRef}
      style={{ margin: 0 }}
    >
      {showOfficeLogo ? (
        <div className="delegate-logo">
          <img
            className="header-logo"
            alt="MyRep"
            src={state?.profile?.fileUrl || logoImageUrl}
          />
          <span style={{ fontFamily: "Lato", marginTop: "-16px" }}>
            {state?.profile?.name || ""}
          </span>
        </div>
      ) : (
        <img className="header-logo" alt="MyRep" src={logoImageUrl} />
      )}

      <nav className="ClinicNav">
        {width >= 530 ? (
          <>
            {navBarLinks?.available?.map((l, index) => (
              <NewNavLink key={index} item={l} />
            ))}
            {componentsLoaded && shouldShowNotifications && (
              <Announcement
                isNotificationsOpen={isNotificationsOpen}
                toggleNotifications={toggleNotifications}
                unReadCount={unReadCount}
                onClose={() => setIsNotificationsOpen(false)}
                refetch={fetchUnreadAnnouncements}
              />
            )}
            <div className="nav-link-separator" />
            {typeof renderLinks === "function" && renderLinks()}
          </>
        ) : (
          <>
            {navBarLinks?.available?.map((l, index) => (
              <NewNavLink key={index} item={l} />
            ))}
            {componentsLoaded && shouldShowNotifications && (
              <Announcement
                isNotificationsOpen={isNotificationsOpen}
                toggleNotifications={toggleNotifications}
                unReadCount={unReadCount}
                onClose={() => setIsNotificationsOpen(false)}
                refetch={fetchUnreadAnnouncements}
              />
            )}
          </>
        )}

        {(width < 530 || navBarLinks?.overflow?.length > 0) && (
          <button
            className={`hamburger-button ${showMobileMenu ? "active" : ""}`}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle menu"
          >
            <div className="hamburger-lines">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        )}
      </nav>

      {/* Drawer */}
      <div
        className={`drawer-overlay ${showMobileMenu ? "active" : ""}`}
        onClick={() => setShowMobileMenu(false)}
      >
        <div
          className={`drawer ${showMobileMenu ? "active" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="drawer-header">
            <img
              src="https://myrepapp-assets.s3-us-west-1.amazonaws.com/271f67d25e15bf1f1ba0e3ce5662b552-newlogo.png"
              alt="MyRep"
            />
            <button
              className={`hamburger-button active`}
              onClick={() => setShowMobileMenu(false)}
              aria-label="Close menu"
            >
              <div className="hamburger-lines">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>
          <div className="drawer-content">
            <div className="drawer-main-links">
              {width < 530 ? (
                <>
                  {links?.map((l) => (
                    <NewNavLinkSidebar
                      key={l.name}
                      item={l}
                      onClick={() => {
                        history.push(l.pathname);
                        setShowMobileMenu(false);
                      }}
                    />
                  ))}
                </>
              ) : (
                navBarLinks?.overflow?.map((l) => (
                  <NewNavLinkSidebar
                    key={l.name}
                    item={l}
                    onClick={() => {
                      history.push(l.pathname);
                      setShowMobileMenu(false);
                    }}
                  />
                ))
              )}
            </div>
            <div className="drawer-bottom-links">
              {currentUser?.id && <MyRepCaresPanel fromDrawer={true} />}
              {isRep && <ReferralsButton fromDrawer={true} />}
              {typeof renderLinks === "function" && (
                <div className="nav-link">{renderLinks(true)}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

NavHeader.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      Img: PropTypes.elementType.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string, // <-- NEW (optional)
    }),
  ).isRequired,
  renderLinks: PropTypes.func,
};

export default NavHeader;
